import { DT } from "../../constants.ts";
import { add, flatBasis, norm, scale, sub, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { addDamageFilter, addOverhealth, aim, applyDamage, applyHeal, applyStatus, damageBarrier, hitscan, isEnemy, lockOn, playersInRadius, removeDamageFilters } from "../../sim/combat.ts";
import { spawnConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, flight, launchProjectile, ownerAlive, ready, reviveAt, secs, simpleProjectile, trigger, useUlt } from "../kitlib.ts";

export const V = { bondA: 0, bondB: 1, bondC: 2, flight: 3, cocoonCd: 4 } as const;
export const PK = { Cluster: pk(HeroId.AtomWarlock, 0) } as const;
export const CK = { Cocoon: ck(HeroId.AtomWarlock, 0) } as const;

const def: HeroDef = {
  id: HeroId.AtomWarlock,
  slug: "atom-warlock",
  name: "Atom Warlock",
  role: Role.Support,
  color: "#e89a2c",
  blurb: "Cosmic support who bursts soul orbs, bonds allies, flies during communion, and rewinds fallen teammates.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.76,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 15,
  reloadTime: 1.7,
  ultCost: 4500,
  abilities: {
    [Slot.Primary]: { name: "Atom Ray", key: "LMB", description: "Fire a precise soul ray. Critical hits recharge Mendstream faster.", cooldown: 0 },
    [Slot.Secondary]: { name: "Cluster Psalm", key: "RMB", description: "Release five fast soul orbs for sustained damage.", cooldown: 0 },
    [Slot.Ability1]: { name: "Soul Stitch", key: "Shift", description: "Bond nearby allies for healing over time, shared damage, and free flight.", cooldown: 40 },
    [Slot.Ability2]: { name: "Mendstream", key: "E", description: "Lock onto an ally, heal them, then bounce healing to nearby allies and yourself.", cooldown: 6, charges: 2 },
    [Slot.Ultimate]: { name: "Karma Reboot", key: "Q", description: "Revive nearby fallen allies at your position with brief protection.", cooldown: 0 },
  },
  passive: { name: "Cocoon Clause", description: "After a long cooldown, lethal damage leaves a golden cocoon that revives you if it survives." },
};

function linkedAllies(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
  return playersInRadius(sim, center, 15, (e) => e === eid || !isEnemy(sim, eid, e), true).map((x) => x.eid).slice(0, 6);
}

function net(sim: Parameters<HeroModule["kit"]["tick"]>[0], e: number | undefined) {
  return e === undefined ? 0 : sim.c.Net.id[e] ?? 0;
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.cocoonCd, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.cocoonCd) > 0) setHv(c, eid, V.cocoonCd, hv(c, eid, V.cocoonCd) - DT);
      if (c.Status.bonded[eid]! <= 0) {
        setHv(c, eid, V.bondA, 0);
        setHv(c, eid, V.bondB, 0);
        setHv(c, eid, V.bondC, 0);
        setHv(c, eid, V.flight, 0);
      } else {
        if (sim.tick % 12 === 0) for (const a of linkedAllies(sim, eid)) applyHeal(sim, eid, a, 3, { silent: true });
        setHv(c, eid, V.flight, 1);
      }
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(10);
        action(sim, eid, ActionKind.Ult, 40);
        sim.emit({ type: "explosion", pos: add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), radius: 35, kind: FxKind.Singularity });
        let revived = 0;
        for (const o of sim.playerEids()) {
          if (revived >= 5 || c.Player.alive[o] || isEnemy(sim, eid, o)) continue;
          const dp = sim.players.get(o)?.deathPos;
          const d = dp ? Math.hypot(dp.x - c.Pos.x[eid]!, dp.y - c.Pos.y[eid]!, dp.z - c.Pos.z[eid]!) : 999;
          if (d <= 35) {
            const { right } = flatBasis(input.yaw);
            reviveAt(sim, o, add(sim.posOf(eid), { x: right.x * (revived - 2) * 0.8, y: 0.1, z: right.z * (revived - 2) * 0.8 }), eid);
            c.Health.hp[o] = c.Health.maxHp[o]! * 0.3;
            addOverhealth(sim, o, c.Health.maxHp[o]! * 0.7, secs(7), eid);
            revived++;
          }
        }
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 24);
        const allies = linkedAllies(sim, eid);
        for (const a of allies) {
          applyStatus(sim, a, "bonded", 6, { source: eid, by: eid });
          removeDamageFilters(sim, a, eid, "atom-soul-stitch");
          addDamageFilter(sim, a, eid, {
            seconds: 6,
            tag: "atom-soul-stitch",
            filter(sim, target, amount, attacker, info) {
              const group = allies.filter((e) => e !== target && sim.c.Player.alive[e] && sim.c.Status.bonded[e]! > 0 && sim.c.Player.team[e] === sim.c.Player.team[target]).slice(0, 3);
              if (!group.length || amount <= 0) return amount;
              const shared = amount * (group.length / (group.length + 1));
              for (const other of group) applyDamage(sim, other, shared / group.length, attacker, { quiet: true, info: { kind: info.kind, sourceKind: info.sourceKind, redirected: true } });
              return amount / (group.length + 1);
            },
          });
        }
        setHv(c, eid, V.bondA, net(sim, allies.find((a) => a !== eid)));
        setHv(c, eid, V.bondB, net(sim, allies.filter((a) => a !== eid)[1]));
        setHv(c, eid, V.bondC, net(sim, allies.filter((a) => a !== eid)[2]));
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 18);
        let target = lockOn(sim, eid, 20, 0.22, true);
        if (target === undefined) target = eid;
        applyHeal(sim, eid, target, 95);
        applyHeal(sim, eid, eid, 35);
        const tp = add(sim.posOf(target), { x: 0, y: 1, z: 0 });
        const bounce = playersInRadius(sim, tp, 20, (e) => e !== target && e !== eid && !isEnemy(sim, eid, e), true).map((x) => x.eid).slice(0, 2);
        for (const b of bounce) applyHeal(sim, eid, b, 95);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Secondary))) {
        c.Weapon.fireCd[eid] = secs(0.58);
        action(sim, eid, ActionKind.AltFire, 16);
        const a = aim(sim, eid);
        for (let i = 0; i < 5; i++) {
          const dir = norm(add(a.dir, scale({ x: Math.sin(i * 2.1), y: Math.cos(i * 1.7) * 0.25, z: Math.cos(i * 2.1) }, 0.018)));
          launchProjectile(sim, eid, PK.Cluster, 100, input, { radius: 0.3, seconds: 1.6, dir });
        }
      } else if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(0.5);
        action(sim, eid, ActionKind.Fire, 7);
        const a = aim(sim, eid);
        const r = hitscan(sim, eid, a.origin, a.dir, { range: 80, damage: 60, falloffStart: 20, falloffEnd: 40, falloffMin: 0.6, canCrit: true, fxKind: FxKind.Singularity });
        if (r.head) c.Abil.cd[eid * 6 + Slot.Ability2] = Math.max(0, c.Abil.cd[eid * 6 + Slot.Ability2]! - secs(1));
      }
    },
    movement(sim, eid, input, mods) {
      if (hv(sim.c, eid, V.flight) > 0) flight(sim, eid, input, mods, { speed: 10, vertical: 6, accel: 35, pitchClimb: true });
    },
    dealtDamage(sim, attacker) {
      const c = sim.c;
      if (c.Status.bonded[attacker]! > 0 && sim.tick % 6 === 0) applyHeal(sim, attacker, attacker, 2, { silent: true });
    },
    onLethal(sim, eid) {
      if (tryAtomCocoon(sim, eid)) {
        sim.c.Health.hp[eid] = 1;
        applyStatus(sim, eid, "invuln", 5, { source: eid });
        return true;
      }
      return false;
    },
    onDeath(sim, eid) {
      for (const a of linkedAllies(sim, eid)) removeDamageFilters(sim, a, eid, "atom-soul-stitch");
    },
  },
  projectiles: {
    [PK.Cluster]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) {
          applyDamage(sim, hit.target, 35, owner, { pos: hit.pos });
          sim.c.Abil.cd[owner * 6 + Slot.Ability2] = Math.max(0, sim.c.Abil.cd[owner * 6 + Slot.Ability2]! - secs(0.6));
        } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 35, owner);
        else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Singularity });
      },
      { color: "#ffe58d", size: 0.24, trail: true },
    ),
  },
  constructs: {
    [CK.Cocoon]: {
      maxHp: 120,
      seconds: 3,
      shape: { kind: "cyl", r: 0.55, h: 1.3 },
      solid: true,
      blocksShots: true,
      persistOnOwnerDeath: true,
      visual: { color: "#ffe58d", shape: "cyl" },
      onDestroy(sim, e, reason) {
        if (reason === "killed") return;
        const owner = sim.c.Construct.owner[e]!;
        if (!sim.players.has(owner) || sim.c.Player.alive[owner]) return;
        reviveAt(sim, owner, add(sim.posOf(e), { x: 0, y: 0.05, z: 0 }), owner);
      },
    },
  },
};

export function tryAtomCocoon(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  if (sim.c.Player.hero[eid] !== HeroId.AtomWarlock || hv(sim.c, eid, V.cocoonCd) > 0) return false;
  setHv(sim.c, eid, V.cocoonCd, 105);
  spawnConstruct(sim, eid, CK.Cocoon, sim.posOf(eid), sim.c.Look.yaw[eid]!, { ownerDeath: "persist" });
  return true;
}

void viewDir;
void sub;
void MoveKind;
export default module;
